import { Injectable } from '@angular/core';
import { StateService } from './state.service';

export interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  timestamp: number;
  autoClose?: boolean;
  duration?: number;
}

export interface NotificationState {
  notifications: Notification[];
  isVisible: boolean;
}

const initialNotificationState: NotificationState = {
  notifications: [],
  isVisible: false,
};

@Injectable({
  providedIn: 'root',
})
export class NotificationStateService extends StateService<NotificationState> {
  // Computed signals for derived state
  readonly notifications = this.selectSlice(state => state.notifications);
  readonly isVisible = this.selectSlice(state => state.isVisible);
  readonly hasNotifications = this.selectSlice(state => state.notifications.length > 0);

  constructor() {
    super(initialNotificationState);
  }

  /**
   * Add a new notification
   */
  addNotification(notification: Omit<Notification, 'id' | 'timestamp'>): void {
    // Check if a notification with the same message and type already exists
    const existingNotification = this.notifications().find(
      n => n.message === notification.message && n.type === notification.type
    );

    // If a duplicate exists, don't add another one
    if (existingNotification) {
      return;
    }

    const newNotification: Notification = {
      ...notification,
      id: this.generateId(),
      timestamp: Date.now(),
      autoClose: notification.autoClose !== false,
      duration: notification.duration || 5000,
    };

    this.setState(state => ({
      notifications: [...state.notifications, newNotification],
      isVisible: true,
    }));

    // Auto-close notification if enabled
    if (newNotification.autoClose) {
      setTimeout(() => {
        this.removeNotification(newNotification.id);
      }, newNotification.duration);
    }
  }

  /**
   * Remove a notification by ID
   */
  removeNotification(id: string): void {
    this.setState(state => ({
      notifications: state.notifications.filter(n => n.id !== id),
    }));

    // Update visibility if no notifications left
    if (this.notifications().length === 0) {
      this.setState({ isVisible: false });
    }
  }

  /**
   * Clear all notifications
   */
  clearNotifications(): void {
    this.setState({
      notifications: [],
      isVisible: false,
    });
  }

  /**
   * Show success notification
   */
  success(message: string, options: Partial<Notification> = {}): void {
    this.addNotification({
      type: 'success',
      message,
      ...options,
    });
  }

  /**
   * Show info notification
   */
  info(message: string, options: Partial<Notification> = {}): void {
    this.addNotification({
      type: 'info',
      message,
      ...options,
    });
  }

  /**
   * Show warning notification
   */
  warning(message: string, options: Partial<Notification> = {}): void {
    this.addNotification({
      type: 'warning',
      message,
      ...options,
    });
  }

  /**
   * Show error notification
   */
  error(message: string, options: Partial<Notification> = {}): void {
    this.addNotification({
      type: 'error',
      message,
      ...options,
    });
  }

  /**
   * Toggle notification visibility
   */
  toggleVisibility(): void {
    this.setState(state => ({ isVisible: !state.isVisible }));
  }

  /**
   * Generate a unique ID for notifications
   */
  private generateId(): string {
    return `notification-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  }
}
